/* eslint-disable no-param-reassign */
import React from 'react';
import Moment from 'moment';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import './SavedQueryTable.scss';
import { Tooltip } from '@mui/material';

import {
  UsersScheduledQueriesFrequencyTypes,
  UsersScheduledQueriesFormatTypes,
  UsersScheduledQueriesDeliveryTypes,
} from '../../utils/constants/enums';

function handleReportSelectChange(
  userQueryOptionId,
  tableIndex,
  event,
  reportSelectDictionary,
  reportSelectArray,
  setReportSelectArray,
  setReportSelectDictionary,
) {
  const objIndex = reportSelectDictionary.findIndex((item) => item.key === userQueryOptionId);
  reportSelectDictionary[objIndex].value = parseInt(event.target.value);
  const newReportSelectArray = [...reportSelectDictionary];
  newReportSelectArray[tableIndex] = parseInt(event.target.value);
  reportSelectArray[tableIndex] = parseInt(event.target.value);
  setReportSelectArray(newReportSelectArray);
  setReportSelectDictionary(reportSelectDictionary);
}

function getUserQueryReportsArrayObject(reportIds, reportNms) {
  const reportIdsArray = reportIds?.split(',');
  const reportNamesArray = reportNms?.split(',');
  if (reportIdsArray == null) {
    return [];
  }
  return reportIdsArray?.map((id, index) => ({
    reportId: id,
    reportNm: reportNamesArray[index],
  }));
}

function getKeyByValue(object, value) {
  const response = Object.keys(object).find((key) => object[key] === parseInt(value));
  return response;
}

const getReportSelectValue = (userQueryOptionId, reportSelectDictionary) =>
  reportSelectDictionary?.find((item) => item.key === userQueryOptionId)?.value;

const columns = (
    reportSelectArray,
    reportSelectDictionary,
    setReportSelectArray,
    setReportSelectDictionary,
  ) => [
    {
      accessorKey: 'userQueryOptionNm', // Replaces 'field'
      header: 'Report', // Replaces 'title'
      Cell: ({ cell }) => (
        <span className="query-name" style={{ whiteSpace: 'pre' }}>
          {cell.getValue().length > 50
            ? `${cell.getValue().substring(0, 47)}...`
            : cell.getValue()}
        </span>
      ), // Replaces 'render'
    },
    {
        accessorKey: 'queryOptionTypeNm',
        header: 'Report Area',
        Cell: ({ cell }) => <span className="query-name">{cell.getValue()}</span>,
        filterVariant: 'select', // Enables dropdown filtering
        filterSelectOptions: [
          { text: 'Market', value: 'Market' },
          { text: 'Risk', value: 'Risk' },
        ], // Dropdown options for filtering
      },
    {
      accessorKey: 'usersQueriesOptionsReportNms',
      header: 'Report Type',

      Cell: ({ row }) => (
        <select
          onChange={(e) =>
            handleReportSelectChange(
              row.original.userQueryOptionId,
              row.index,
              e,
              reportSelectDictionary,
              reportSelectArray,
              setReportSelectArray,
              setReportSelectDictionary,
            )
          }
          value={reportSelectArray[row.index]}
          name="Reports"
          className="reportType__select"
          data-cy="reportType_select"
        >
          {getUserQueryReportsArrayObject(
            row.original.usersQueriesOptionsReportIds,
            row.original.usersQueriesOptionsReportNms,
          ).map((report, index) => (
            <option key={index} value={index}>
              {report?.reportNm}
            </option>
          ))}
        </select>
      ),
    },
    {
      accessorKey: 'usersQueriesOptionsStateNms',
      header: 'State',
      Cell: ({ cell }) => (
        <span className="query-name">
          {cell.getValue()?.length > 50
            ? `${cell.getValue().substring(0, 47)}...`
            : cell.getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'userQueryOptionDesc',
      header: 'Description',
      Cell: ({ cell }) => (
        <span className="query-name" style={{ whiteSpace: 'pre' }}>
          {cell.getValue()?.length > 50
            ? `${cell.getValue().substring(0, 47)}...`
            : cell.getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'userScheduledQueryFrequencyTypeId',
      header: 'Frequency',
      Cell: ({ cell }) => (
        <span className="query-name">
          {getKeyByValue(UsersScheduledQueriesFrequencyTypes, cell.getValue())}
        </span>
      ),
      enableColumnFilter: false,
    },
    {
      accessorKey: 'userScheduledQueryFormatTypeId',
      header: 'Format',
      Cell: ({ cell }) => (
        <span className="query-name">
          {getKeyByValue(UsersScheduledQueriesFormatTypes, cell.getValue())}
        </span>
      ),
      enableColumnFilter: false,
    },
    {
      accessorKey: 'userScheduledQueryDeliveryTypeId',
      header: 'Delivery',
      Cell: ({ cell }) => (
        <span className="query-name">
          {getKeyByValue(UsersScheduledQueriesDeliveryTypes, cell.getValue())}
        </span>
      ),
      enableColumnFilter: false,
    },
    {
      accessorKey: 'lastRun',
      header: 'Last Run',
      Cell: ({ cell }) => (
        <span className="query-name">
          {cell.getValue() ? Moment(cell.getValue()).format('MM-DD-YYYY') : ''}
        </span>
      ),
      enableColumnFilter: false,
    },
  ];


const actions = (
    row,
    prepareAndRunQuery,
    prepareAndEditQuery,
    reportSelectDictionary,
    setShowConfirmationModal,
    setCurrentRow,
  ) => (
    <div style={{ display: 'flex', gap: '8px' }}>
      <Tooltip title="Run Online">
        <button
          onClick={() =>
            prepareAndRunQuery({
              reportIdSelectionIndex: getReportSelectValue(row.original.userQueryOptionId, reportSelectDictionary),
              userQueryOptionId: row.original.userQueryOptionId,
              userQueryOptionNm: row.original.userQueryOptionNm,
              usersQueriesOptionsReportIds: row.original.usersQueriesOptionsReportIds,
              savedQueryJSONUnparsed: row.original.userQueryOptionJSON,
              queryOptionTypeId: row.original.queryOptionTypeId,
            })
          }
          className="action-button"
          data-cy="saved-query-table-run-online-button" // Added data-cy attribute
          aria-label="Run Online" // Add an accessible label
          type="button" // Explicitly set the button type
        >
          <PlayCircleOutline />
        </button>
      </Tooltip>
      <Tooltip title="Edit">
        <button
          onClick={() =>
            prepareAndEditQuery({
              userQueryOptionId: row.original.userQueryOptionId,
              userQueryOptionNm: row.original.userQueryOptionNm,
              usersQueriesOptionsReportIds: row.original.usersQueriesOptionsReportIds,
              queryOptionTypeId: row.original.queryOptionTypeId,
              userQueryOptionDesc: row.original.userQueryOptionDesc,
            })
          }
          className="action-button"
          data-cy="saved-query-table-edit-button"
          aria-label="Edit" // Add an accessible label
          type="button" // Explicitly set the button type
        >
          <EditIcon />
        </button>
      </Tooltip>
      <Tooltip title="Delete">
        <button
          onClick={() => {
            setCurrentRow(row.original);
            setShowConfirmationModal(true);
          }}
          className="action-button"
          data-cy="saved-query-table-delete-button"
          aria-label="Delete" // Add an accessible label
          type="button" // Explicitly set the button type
        >
          <DeleteIcon />
        </button>
      </Tooltip>
    </div>
  );

export { columns, getReportSelectValue, getUserQueryReportsArrayObject, actions };